/**
 * Floating "Need Help?" / "Help Inbox" widget.
 *
 * - Requesters (social_actor, investor, guest): can open a chat panel,
 *   start a new conversation, pick a specific helper or "any available",
 *   and exchange messages with PMs / Admins.
 *
 * - Helpers (administrator, project_manager): see an Inbox of help threads
 *   assigned to them + any unclaimed open thread. Replying to an unclaimed
 *   thread auto-claims it. A heartbeat ping every 30 s keeps them visible
 *   on the requesters' "Online now" list.
 *
 * Polls /api/help/poll every 5 s while the widget is open or when an unread
 * badge would otherwise go stale.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  MessageCircle, X, Send, ArrowLeft, CheckCircle2, Circle, Loader2,
  Plus, ShieldCheck, Briefcase, User as UserIcon, Minus,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;
const POLL_MS = 5000;
const HEARTBEAT_MS = 30000;

const HELPER_ROLES = new Set(['administrator', 'admin', 'project_manager']);
const REQUESTER_ROLES = new Set(['social_actor', 'investor', 'guest']);

const roleIcon = (role) => {
  if (role === 'administrator' || role === 'admin') return <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />;
  if (role === 'project_manager') return <Briefcase className="h-3.5 w-3.5 text-emerald-700" />;
  return <UserIcon className="h-3.5 w-3.5 text-gray-500" />;
};

const friendlyRole = (role) => ({
  administrator: 'Administrator',
  admin: 'Administrator',
  project_manager: 'Project Manager',
  social_actor: 'Social Actor',
  investor: 'Investor',
  guest: 'Guest',
}[role] || role || '');

const HelpWidget = ({ user }) => {
  const role = user?.role === 'admin' ? 'administrator' : user?.role;
  const isHelper = HELPER_ROLES.has(role);
  const isRequester = REQUESTER_ROLES.has(role);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'compose' | 'thread'
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [helpers, setHelpers] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeHelper, setComposeHelper] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);

  const scrollerRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);

  // ── Drag-to-reposition state ──────────────────────────────────────────
  // The floating bubble (and the panel when open) can be dragged anywhere
  // on screen. Position is persisted per-user in localStorage so it
  // survives reloads. The default sits in the bottom-right corner
  // (24 px in from each edge).
  const POS_STORAGE_KEY = `help_widget_pos_${user?.id || 'anon'}`;
  const MIN_STORAGE_KEY = `help_widget_minimized_${user?.id || 'anon'}`;
  const DEFAULT_POS = { right: 24, bottom: 24 };
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem(POS_STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        if (typeof p?.right === 'number' && typeof p?.bottom === 'number') return p;
      }
    } catch { /* ignore */ }
    return DEFAULT_POS;
  });
  // Minimised state — collapses the floating bubble to a tiny dot in the
  // same corner so it gets out of the way without disappearing entirely.
  // Persisted per-user so the preference survives navigation/reload.
  const [minimized, setMinimized] = useState(() => {
    try { return localStorage.getItem(MIN_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const setMinimizedPersist = (v) => {
    setMinimized(v);
    try { localStorage.setItem(MIN_STORAGE_KEY, v ? '1' : '0'); } catch { /* ignore */ }
  };
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startPos: null, moved: false });

  const onDragStart = (e) => {
    // Skip drag for non-primary mouse buttons / touch is fine via pointer events
    if (e.button !== undefined && e.button !== 0) return;
    // Skip drag when the pointer originates from an interactive control inside
    // the header (back button, close button, etc.). Without this, the pointer
    // capture below would steal the subsequent `click` event from the button.
    if (e.target && typeof e.target.closest === 'function' && e.target.closest('button, a, input, textarea, select')) {
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPos: { ...pos },
      moved: false,
    };
  };

  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 4) return; // ignore tiny jitters → treat as click
    d.moved = true;
    // We store position as offsets from the bottom-right edge to keep the
    // widget anchored sensibly across viewport resizes.
    const nextRight = Math.max(0, d.startPos.right - dx);
    const nextBottom = Math.max(0, d.startPos.bottom - dy);
    // Cap so the widget can't leave the viewport (assume ~120 px wide button).
    const maxRight = Math.max(0, window.innerWidth - 64);
    const maxBottom = Math.max(0, window.innerHeight - 64);
    setPos({
      right: Math.min(maxRight, nextRight),
      bottom: Math.min(maxBottom, nextBottom),
    });
  };

  const onDragEnd = (e) => {
    const d = dragRef.current;
    if (!d.active) return; // never started — nothing to release
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    dragRef.current.active = false;
    if (d.moved) {
      try { localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
    }
    return d.moved; // caller uses this to suppress click
  };

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Initialize audio chime once (uses an inline data-URI so no extra asset)
  useEffect(() => {
    // A small soft bell — synthesised with Web Audio so we don't ship a binary
    audioRef.current = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.5);
      } catch {
        /* sound is best-effort, never crash */
      }
    };
  }, []);

  // Ask once for browser-notification permission (best-effort)
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
  }, []);

  const notifyExternally = useCallback((title, body) => {
    // Play chime
    if (audioRef.current) audioRef.current();
    // Browser notification (only if tab is not focused and permission granted)
    try {
      if (
        typeof Notification !== 'undefined'
        && Notification.permission === 'granted'
        && (typeof document === 'undefined' || document.visibilityState !== 'visible')
      ) {
        const n = new Notification(title, { body, tag: 'isisp-help', icon: '/favicon.ico' });
        n.onclick = () => { window.focus(); setOpen(true); n.close(); };
      }
    } catch {
      /* ignore */
    }
  }, []);

  // ───────────────────────── Keyboard shortcut ─────────────────────────
  // Ctrl+Shift+H (Cmd+Shift+H on Mac) toggles the widget between two states:
  //   • open → close the panel and minimise to a tiny dot
  //   • closed/minimised → restore the bubble and open the panel
  // Ignored while the user is typing in a form field so it doesn't fight
  // with browser shortcuts (Ctrl+Shift+H is also Firefox's history shortcut
  // — we preventDefault so ours wins inside our app surface).
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      const key = (e.key || '').toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (!(mod && e.shiftKey && key === 'h')) return;
      // Don't hijack typing inside an editable field (other than ours)
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      // Allow inside our own widget controls so the shortcut still works when
      // focus is inside the message composer.
      const insideWidget = e.target?.closest?.('[data-testid="help-panel"], [data-testid="help-bubble"], [data-testid="help-bubble-dot"]');
      if (isEditing && !insideWidget) return;
      e.preventDefault();
      if (open) {
        setOpen(false);
        setMinimizedPersist(true);
      } else {
        setMinimizedPersist(false);
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  // ───────────────────────── Polling ─────────────────────────
  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/help/poll`, { headers });
      const next = res.data.unread || 0;
      const nextThreads = res.data.threads || [];

      // Detect a real increase since the last poll and chime if panel is closed
      if (next > prevUnreadRef.current && !open) {
        const newest = nextThreads[0];
        const title = isHelper ? 'New help request' : 'New help reply';
        const body = newest?.last_message_preview || 'You have a new help message';
        notifyExternally(title, body);
      }
      prevUnreadRef.current = next;
      setUnread(next);
      // The /poll endpoint only returns threads with new activity (unread or
      // unclaimed). If we let it overwrite `threads` while the user is on the
      // list view, closed/quiet conversations would silently disappear from
      // the list every 5 seconds. So when the widget is open on the list,
      // we keep the full list (refreshed by loadFullThreads on a slower cadence)
      // and only update the unread counter.
      if (!(open && view === 'list')) {
        setThreads(nextThreads);
      }
    } catch {
      /* silent — networking blips shouldn't spam the user */
    }
  }, [token, open, view, isHelper, notifyExternally]);

  useEffect(() => {
    if (!user) return;
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [user, poll]);

  // ─────────────── Heartbeat (helpers only) ───────────────
  useEffect(() => {
    if (!isHelper || !token) return;
    const ping = () => axios.post(`${API}/api/help/heartbeat`, {}, { headers }).catch(() => {});
    ping();
    const id = setInterval(ping, HEARTBEAT_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHelper, token]);

  // ─────────────── Fetch online helpers when opening compose ───────────────
  useEffect(() => {
    if (!open || view !== 'compose' || !isRequester) return;
    axios
      .get(`${API}/api/help/online-helpers`, { headers })
      .then((res) => setHelpers(res.data.helpers || []))
      .catch(() => setHelpers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, isRequester]);

  // ─────────────── Full thread fetch (with messages) ───────────────
  const loadFullThreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/help/threads`, { headers });
      setThreads(res.data.threads || []);
    } catch {
      /* silent */
    }
  }, [token]);

  // Refresh the full thread list whenever the user lands on the list view,
  // AND on a slower interval (every 15 s) while they stay on it. This keeps
  // closed conversations visible alongside live activity.
  useEffect(() => {
    if (!(open && view === 'list')) return;
    loadFullThreads();
    const id = setInterval(loadFullThreads, 15000);
    return () => clearInterval(id);
  }, [open, view, loadFullThreads]);

  const openThread = async (thread) => {
    setActiveThread(thread);
    setView('thread');
    setLoadingThread(true);
    try {
      const res = await axios.get(`${API}/api/help/threads/${thread.id}`, { headers });
      setActiveThread(res.data.thread);
      setMessages(res.data.messages || []);
      poll(); // refresh badge
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load conversation');
    } finally {
      setLoadingThread(false);
    }
  };

  // Polling for the open thread itself
  useEffect(() => {
    if (view !== 'thread' || !activeThread) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/help/threads/${activeThread.id}`, { headers });
        setMessages((prev) => {
          const fresh = res.data.messages || [];
          // If a new message arrived from someone else, ping the user
          if (fresh.length > prev.length) {
            const latest = fresh[fresh.length - 1];
            if (latest.sender_id !== user.id) {
              if (audioRef.current) audioRef.current();
            }
          }
          return fresh;
        });
        setActiveThread(res.data.thread);
      } catch {
        /* silent */
      }
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeThread?.id]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, view]);

  // ───────────────────────── Actions ─────────────────────────
  const startThread = async () => {
    if (!composeBody.trim()) {
      toast.error('Please type your question first');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/help/threads`,
        {
          subject: composeSubject.trim() || undefined,
          initial_message: composeBody.trim(),
          helper_id: composeHelper || undefined,
        },
        { headers }
      );
      toast.success('Help request sent');
      setComposeSubject('');
      setComposeBody('');
      setComposeHelper('');
      await openThread(res.data.thread);
      await loadFullThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send help request');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!draft.trim() || !activeThread) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/help/threads/${activeThread.id}/messages`,
        { body: draft.trim() },
        { headers }
      );
      setMessages((m) => [...m, res.data.message]);
      setDraft('');
      loadFullThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const closeThread = async () => {
    if (!activeThread) return;
    if (!window.confirm('Close this help conversation?')) return;
    try {
      await axios.post(`${API}/api/help/threads/${activeThread.id}/close`, {}, { headers });
      toast.success('Conversation closed');
      setActiveThread({ ...activeThread, status: 'closed' });
      loadFullThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not close conversation');
    }
  };

  const reopenThread = async () => {
    if (!activeThread) return;
    try {
      const res = await axios.post(`${API}/api/help/threads/${activeThread.id}/reopen`, {}, { headers });
      toast.success('Conversation reopened');
      setActiveThread({ ...activeThread, status: res.data?.status || 'active' });
      loadFullThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reopen conversation');
    }
  };


  // Don't render for unauthenticated users or unknown roles
  if (!user || (!isHelper && !isRequester)) return null;

  const formatTime = (iso) =>
    iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '';

  const ownMessage = (msg) => msg.sender_id === user.id;

  return (
    <>
      {/* Minimised dot — shown when user has collapsed the bubble. Tiny so
         it doesn't get in the way; click restores the full bubble. */}
      {!open && minimized && (
        <button
          onClick={() => setMinimizedPersist(false)}
          style={{ right: `${pos.right}px`, bottom: `${pos.bottom}px` }}
          className="fixed z-50 bg-blue-800 hover:bg-blue-900 text-white rounded-full h-4 w-4 shadow-md flex items-center justify-center transition-transform hover:scale-125 cursor-pointer"
          data-testid="help-bubble-dot"
          aria-label="Restore help inbox"
          title={`${isHelper ? 'Help inbox' : 'Need help?'}${unread > 0 ? ` · ${unread} unread` : ''} · Ctrl+Shift+H to restore`}
        >
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-red-700 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center font-bold leading-none"
              data-testid="help-dot-unread-badge"
            >
              {unread > 9 ? '9' : unread}
            </span>
          )}
        </button>
      )}

      {/* Floating bubble (draggable) — outer is a plain container so we
         don't have nested interactive controls. The drag handlers live on
         it; the click-to-open and minimise actions are explicit child
         buttons. */}
      {!open && !minimized && (
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          style={{ right: `${pos.right}px`, bottom: `${pos.bottom}px`, touchAction: 'none' }}
          className="fixed z-50 bg-blue-900 hover:bg-blue-950 text-white rounded-full shadow-lg flex items-center transition-transform hover:scale-105 cursor-grab active:cursor-grabbing select-none"
          data-testid="help-bubble"
          title="Drag to reposition · Ctrl+Shift+H to toggle"
        >
          <button
            type="button"
            onClick={() => {
              if (dragRef.current?.moved) return;
              setOpen(true);
            }}
            className="relative flex items-center gap-1 pl-4 pr-3 py-2.5 rounded-l-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            data-testid="help-bubble-open"
            aria-label={isHelper ? (unread > 0 ? `Help inbox (${unread} unread)` : 'Help inbox') : (unread > 0 ? `Need help — ${unread} unread replies` : 'Need help?')}
          >
            <MessageCircle className="h-5 w-5 pointer-events-none" aria-hidden="true" />
            {!isHelper && <span className="hidden sm:inline font-medium pointer-events-none">Need help?</span>}
            {isHelper && <span className="hidden sm:inline font-medium pointer-events-none">Help inbox</span>}
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-red-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold pointer-events-none"
                data-testid="help-unread-badge"
                aria-hidden="true"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setMinimizedPersist(true)}
            className="h-9 w-9 mr-1 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors"
            data-testid="help-bubble-minimize"
            aria-label="Minimise help bubble to a small dot"
            title="Minimise to a small dot · Ctrl+Shift+H"
          >
            <Minus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Panel — anchored to the same position; the header is a drag-handle */}
      {open && (
        <div
          style={{ right: `${pos.right}px`, bottom: `${pos.bottom}px` }}
          className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 w-[380px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden"
          data-testid="help-panel"
        >
          {/* Header (drag-handle to reposition the open panel) */}
          <div
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 flex items-center justify-between cursor-grab active:cursor-grabbing select-none"
            style={{ touchAction: 'none' }}
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
            title="Drag to reposition the help panel"
          >
            <div className="flex items-center gap-2 min-w-0">
              {view !== 'list' && (
                <button
                  onClick={() => { setView('list'); setActiveThread(null); }}
                  className="hover:bg-white/10 rounded p-1"
                  aria-label="Back to help inbox"
                  data-testid="help-back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <MessageCircle className="h-4 w-4 flex-shrink-0" />
              <span className="font-semibold truncate">
                {view === 'compose' && 'New help request'}
                {view === 'thread' && (activeThread?.subject || 'Conversation')}
                {view === 'list' && (isHelper ? 'Help Inbox' : 'Live Help')}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => { setOpen(false); setMinimizedPersist(true); }}
                className="hover:bg-white/10 rounded p-1"
                data-testid="help-minimize-btn"
                title="Minimise to a small dot · Ctrl+Shift+H"
                aria-label="Minimise"
              >
                <Minus className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} className="hover:bg-white/10 rounded p-1" data-testid="help-close-btn" aria-label="Close help panel (keeps the bubble visible)" title="Close (keeps the bubble visible)">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" ref={scrollerRef}>
            {view === 'list' && (
              <div className="p-3 space-y-2">
                {isRequester && (
                  <Button
                    onClick={() => setView('compose')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="help-new-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Start a new conversation
                  </Button>
                )}
                {threads.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-8">
                    {isHelper
                      ? 'No active help requests right now.'
                      : 'You have no conversations yet. Start one above.'}
                  </div>
                ) : (
                  threads.map((t) => {
                    const myUnread = isHelper ? (t.helper_id === user.id ? t.unread_for_helpers : 0) : t.unread_for_requester;
                    const isUnclaimed = isHelper && !t.helper_id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => openThread(t)}
                        className={`w-full text-left rounded-lg border p-3 hover:bg-gray-50 transition ${
                          myUnread > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                        }`}
                        data-testid={`help-thread-${t.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm text-gray-900 truncate">{t.subject}</p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {t.status === 'closed' && <Badge variant="outline" className="text-xs">Closed</Badge>}
                            {isUnclaimed && <Badge className="bg-amber-100 text-amber-800 text-xs">Unclaimed</Badge>}
                            {myUnread > 0 && (
                              <Badge className="bg-red-600 text-white text-xs">{myUnread}</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-1">{t.last_message_preview}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            {roleIcon(isHelper ? t.requester_role : (t.helper_id ? 'administrator' : 'guest'))}
                            {isHelper ? `${t.requester_name} (${friendlyRole(t.requester_role)})` : (t.helper_name ? `with ${t.helper_name}` : 'Waiting for a helper…')}
                          </span>
                          <span className="text-xs text-gray-500">{formatTime(t.last_message_at)}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {view === 'compose' && isRequester && (
              <div className="p-3 space-y-3">
                <p className="text-xs text-gray-600">
                  A Project Manager or Administrator will respond as soon as they're available.
                </p>

                {helpers.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Send to (optional)</label>
                    <select
                      className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                      value={composeHelper}
                      onChange={(e) => setComposeHelper(e.target.value)}
                      data-testid="help-recipient-select"
                    >
                      <option value="">Any available helper</option>
                      {helpers.map((h) => (
                        <option key={h.user_id} value={h.user_id}>
                          {h.user_name} — {friendlyRole(h.role)} (online)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-700" />
                      {helpers.length} helper{helpers.length === 1 ? '' : 's'} online now
                    </p>
                  </div>
                )}
                {helpers.length === 0 && (
                  <p className="text-xs text-amber-700 flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    <Circle className="h-3 w-3" />
                    Nobody is online right now — your message will be picked up as soon as a helper comes online.
                  </p>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Subject (optional)</label>
                  <Input
                    placeholder="What do you need help with?"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    maxLength={140}
                    data-testid="help-subject-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Message</label>
                  <Textarea
                    rows={4}
                    placeholder="Describe what you're trying to do and what's going wrong…"
                    value={composeBody}
                    onChange={(e) => setComposeBody(e.target.value)}
                    data-testid="help-body-input"
                  />
                </div>

                <Button
                  onClick={startThread}
                  disabled={sending || !composeBody.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="help-send-btn"
                >
                  {sending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Send help request
                </Button>
              </div>
            )}

            {view === 'thread' && activeThread && (
              <div className="flex flex-col h-full">
                <div className="px-3 py-2 border-b bg-gray-50 text-xs text-gray-600 flex items-center justify-between flex-wrap gap-1">
                  <span className="flex items-center gap-1">
                    {roleIcon(isHelper ? activeThread.requester_role : 'administrator')}
                    {isHelper
                      ? `${activeThread.requester_name} (${friendlyRole(activeThread.requester_role)})`
                      : (activeThread.helper_name ? `${activeThread.helper_name}` : 'Waiting for a helper…')}
                  </span>
                  {activeThread.status !== 'closed' && (
                    <button
                      onClick={closeThread}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="help-close-thread"
                    >
                      Close conversation
                    </button>
                  )}
                  {activeThread.status === 'closed' && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">Closed</Badge>
                      <button
                        onClick={reopenThread}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="help-reopen-thread"
                      >
                        Reopen
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                  {loadingThread ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>
                  ) : (
                    messages.map((m) => {
                      const mine = ownMessage(m);
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 ${
                              mine ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
                            }`}
                            data-testid={`help-msg-${m.id}`}
                          >
                            {!mine && (
                              <p className="text-xs uppercase tracking-wide opacity-75 mb-0.5 flex items-center gap-1">
                                {roleIcon(m.sender_role)} {m.sender_name}
                              </p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                            <p className={`text-xs mt-1 ${mine ? 'text-blue-100' : 'text-gray-500'}`}>{formatTime(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {activeThread.status !== 'closed' && (
                  <div className="p-2 border-t bg-white flex items-end gap-2">
                    <Textarea
                      rows={2}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type a message…"
                      className="resize-none text-sm"
                      data-testid="help-draft-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sending || !draft.trim()}
                      className="bg-blue-600 hover:bg-blue-700 px-3"
                      data-testid="help-send-msg-btn"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpWidget;
